import React, { useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useEntitlements } from '../hooks/useEntitlements';
import { hubForPath } from './navModel';
import SubTabs from './SubTabs';
import { useScrollRow } from './useScrollRow';
import './nav.css';

/**
 * The hub's tab bar, drawn by Layout directly above the EXISTING page when the new navigation is on and the
 * current route belongs to a hub. Tabs are links to existing URLs (nothing is embedded or moved), styled with
 * the same chip classes as today's SectionChipBar. Only tabs the package includes are listed. Active tab =
 * the one that owns the current route. A tab with `chips` (الإعلانات) gets a second row.
 */
export default function HubTabBar() {
  const { t } = useTranslation();
  const { pathname, state } = useLocation();
  const { has, features, ready } = useEntitlements();
  const rowRef = useRef(null);
  const hit = ready ? hubForPath(pathname, { has, features }, state) : null;
  useScrollRow(rowRef, hit?.tab?.id);
  if (!ready) return null;

  // One tab = nothing to switch between; a one-tab hub draws no bar (the sidebar item is already its name).
  if (!hit || (hit.tabs.length < 2 && hit.chips.length < 2)) return null;

  const chipClass = (active) => `pp-chip ${active ? 'pp-chip-active' : ''}`;
  const chipActive = (c) => c.prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'));

  return (
    <div className="pp-chip-bar pp-hub-bar" data-hub={hit.hubId}>
      {hit.tabs.length > 1 && (
        <div className="pp-scrollfade">
          <div ref={rowRef} className="pp-chip-bar-inner pp-scrollrow">
            {hit.tabs.map((tab) => (
              <NavLink key={tab.id} to={tab.path} className={chipClass(tab.id === hit.tab.id)} data-hub-tab={tab.id}
                aria-current={tab.id === hit.tab.id ? 'page' : undefined}>
                {t(tab.labelKey)}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      {/* level 2 (at most one secondary row, the same compact style as every other sub-tab row) */}
      <SubTabs
        items={hit.chips.map((c) => ({ id: c.id, label: t(c.labelKey), to: c.path, active: chipActive(c) }))}
      />
    </div>
  );
}
